import {
  AppModal,
  Button,
  Form,
  FormSubmitButton,
  LabelText,
  TextFormField,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { AddContactFormField } from "@/components/ui/FormFields/AddContactFormField/AddContactFormField";
import { DateFormField } from "@/components/ui/FormFields/DateFormField/DateFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { MatterContactConnectionModalPreview } from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionModalPreview";
import { useUser } from "@/components/UserProvider";
import {
  useCreateMatterShareFolder,
  useCreateMatterShareFolderFile,
} from "@/lib/data/react-query/matters/matter-share-folder";
import type { CreateMatterShareFolderPayload } from "@/lib/data/requests/matters/matter-share-folder";
import { ContactConnectionInFormSchema } from "@/lib/data/schemas";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type {
  APIResponse,
  Contact,
  Matter,
  MatterFile,
  MatterShareFolderFile,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { getConnectionFormContact } from "@/lib/utils/connections/item-contact-connections";
import { getFileIcon } from "@/lib/utils/matterFile";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { SelectDocumentModal } from "../signature-requests/SelectDocumentModal";

const baseSchema = z.object({
  matterShareFolderLabel: z
    .string()
    .trim()
    .min(1, { message: "Label is required" })
    .max(100, "Label must be 100 characters or less"),
  matterShareFolderExpirationDate: z
    .string()
    .date()
    .refine((value) => value, { message: "Expiration date is required" }),
  matterFileIds: z.array(z.string()).min(1, "At least one file is required"),
});

const formContactSchema = ContactConnectionInFormSchema.unwrap().passthrough();
type ShareFolderConnectionPayload =
  CreateMatterShareFolderPayload["contactConnections"][number];

const createShareFolderFormSchema = baseSchema.extend({
  matterContactConnections: z
    .array(formContactSchema)
    .min(1, "At least one recipient is required"),
});

const editShareFolderFormSchema = baseSchema.extend({
  matterContactConnections: z.array(formContactSchema),
});

type ShareFolderFormValues =
  | z.infer<typeof createShareFolderFormSchema>
  | z.infer<typeof editShareFolderFormSchema>;

const defaultFormValues: ShareFolderFormValues = {
  matterShareFolderLabel: "",
  matterShareFolderExpirationDate: "",
  matterFileIds: [],
  matterContactConnections: [],
};

const SHARE_FOLDER_MODAL_FORM_ID = "create-or-edit-share-folder-modal-form";

function shareFolderRecipientContactPreview(
  contact: Contact,
  matterId: string,
  onRemoveFromForm?: () => void,
) {
  return (
    <MatterContactConnectionModalPreview
      contactId={contact.id}
      previewContact={contact}
      matterId={matterId}
      onRemoveContact={
        onRemoveFromForm
          ? () => {
              onRemoveFromForm();
            }
          : undefined
      }
      visualVariant="white"
    />
  );
}

interface CreateOrEditShareFolderModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  matterId: string;
  title: string;
  description?: string;
  initialSelectedFileIds?: string[];
  matterData?: APIResponse<Matter>;
  editing?: boolean;
  shareFolderId?: string;
}

export const CreateOrEditShareFolderModal = ({
  open,
  setOpen,
  matterId,
  title,
  description,
  initialSelectedFileIds,
  matterData,
  editing = false,
  shareFolderId,
}: CreateOrEditShareFolderModalProps) => {
  const { hasActionPermission } = useUser();
  const canCreateShareFolderFiles = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterShareFolderFile,
    ACTION_PERMISSION_VALUES.create,
  );
  const [isSelectDocumentOpen, setIsSelectDocumentOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<MatterFile[]>([]);

  const matter = matterData?.data;

  const existingShareFolder = useMemo(() => {
    if (!editing || !shareFolderId || !matter) return undefined;
    return matter.shareFolders.find((folder) => folder.id === shareFolderId);
  }, [editing, matter, shareFolderId]);

  const existingShareFolderFiles = useMemo<MatterShareFolderFile[]>(() => {
    if (!editing || !shareFolderId || !matter) return [];

    const shareFolderFiles = matter.shareFolderFiles;

    if (!Array.isArray(shareFolderFiles)) {
      return [];
    }

    return shareFolderFiles.filter(
      (file) => file.shareFolderId === shareFolderId,
    );
  }, [editing, matter, shareFolderId]);

  const existingShareFolderFileIds = useMemo(() => {
    return new Set(existingShareFolderFiles.map((file) => file.fileId));
  }, [existingShareFolderFiles]);

  const createDefaultValues = useMemo(
    () => ({
      ...defaultFormValues,
      matterFileIds: initialSelectedFileIds ?? [],
    }),
    [initialSelectedFileIds],
  );

  const editDefaultValues = useMemo(() => {
    if (!existingShareFolder) return { ...defaultFormValues };

    const contacts = existingShareFolder.connections
      .map((connection) => getConnectionFormContact(connection))
      .filter((contact): contact is NonNullable<typeof contact> =>
        Boolean(contact),
      );

    return {
      matterShareFolderLabel: existingShareFolder.label,
      matterShareFolderExpirationDate: existingShareFolder.expirationDate,
      matterFileIds: [],
      matterContactConnections: contacts,
    } satisfies ShareFolderFormValues;
  }, [existingShareFolder]);

  const form = useForm<ShareFolderFormValues>({
    resolver: zodResolver(
      editing ? editShareFolderFormSchema : createShareFolderFormSchema,
    ),
    defaultValues: editing ? editDefaultValues : createDefaultValues,
    mode: "onSubmit",
  });

  const wasOpenRef = useRef(false);

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;

    if (!justOpened) return;

    if (editing) {
      form.reset(editDefaultValues);
      setSelectedFiles([]);
      return;
    }

    form.reset(createDefaultValues);
    if (matter && (initialSelectedFileIds?.length ?? 0) > 0) {
      const files = matter.files.filter((f) =>
        initialSelectedFileIds?.includes(f.id),
      );
      setSelectedFiles(files);
    } else {
      setSelectedFiles([]);
    }
  }, [
    open,
    editing,
    form,
    editDefaultValues,
    createDefaultValues,
    matter,
    initialSelectedFileIds,
  ]);

  const createMatterShareFolder = useCreateMatterShareFolder();
  const createMatterShareFolderFile = useCreateMatterShareFolderFile();

  const handleDocumentSelected = (files: MatterFile[]) => {
    if (files.length === 0) return;

    const filteredCandidates = [...selectedFiles, ...files].filter((file) =>
      editing ? !existingShareFolderFileIds.has(file.id) : true,
    );

    const uniqueMap = new Map<string, MatterFile>();
    filteredCandidates.forEach((file) => {
      uniqueMap.set(file.id, file);
    });

    const uniqueFiles = Array.from(uniqueMap.values());

    if (uniqueFiles.length === selectedFiles.length) {
      setIsSelectDocumentOpen(false);
      return;
    }

    setSelectedFiles(uniqueFiles);
    form.setValue(
      "matterFileIds",
      uniqueFiles.map((f) => f.id),
      { shouldDirty: true },
    );
    setIsSelectDocumentOpen(false);
  };

  const handleRemoveDocument = (fileId: string) => {
    setSelectedFiles((prev) => {
      const next = prev.filter((f) => f.id !== fileId);
      form.setValue(
        "matterFileIds",
        next.map((f) => f.id),
        { shouldDirty: true },
      );
      return next;
    });
  };

  const handleSubmit = async (_values: ShareFolderFormValues) => {
    if (!canCreateShareFolderFiles) return;
    setIsSubmitting(true);

    try {
      if (editing) {
        if (!shareFolderId) {
          console.error(
            "shareFolderId is required when editing a share folder",
          );
          return;
        }

        const newFileIds = Array.from(
          new Set(
            _values.matterFileIds.filter(
              (id) => !existingShareFolderFileIds.has(id),
            ),
          ),
        );

        if (newFileIds.length === 0) return;

        const results = await Promise.allSettled(
          newFileIds.map((matterFileId) =>
            createMatterShareFolderFile.mutateAsync({
              matterId,
              shareFolderId,
              fileId: matterFileId,
            }),
          ),
        );

        const failures = results.filter((r) => r.status === "rejected");
        if (failures.length > 0) {
          console.error(
            `Failed to add ${failures.length} file(s) to the share folder`,
            failures,
          );
          throw new Error("Some file additions failed");
        }

        setSelectedFiles([]);
        form.reset({ ...editDefaultValues, matterFileIds: [] });
        setOpen(false);
        return;
      }

      const contactConnections: ShareFolderConnectionPayload[] =
        _values.matterContactConnections.map((connection) => ({
          contactId: connection.id,
          label: "Accessor",
        }));

      const shareFolderResponse = await createMatterShareFolder.mutateAsync({
        matterId,
        label: _values.matterShareFolderLabel,
        expirationDate: _values.matterShareFolderExpirationDate,
        contactConnections,
      });

      const fileResults = await Promise.allSettled(
        _values.matterFileIds.map((matterFileId) =>
          createMatterShareFolderFile.mutateAsync({
            matterId,
            shareFolderId: shareFolderResponse.data.id,
            fileId: matterFileId,
          }),
        ),
      );

      const fileFailures = fileResults.filter((r) => r.status === "rejected");
      if (fileFailures.length > 0) {
        console.error(
          `Failed to add ${fileFailures.length} file(s)`,
          fileFailures,
        );
        throw new Error("Some file additions failed");
      }

      form.reset(defaultFormValues);
      setSelectedFiles([]);
    } catch (err) {
      console.error("Error submitting share folder form:", err);
    } finally {
      setOpen(false);
      setIsSubmitting(false);
    }
  };

  const existingFileCount = existingShareFolderFiles.length;

  const shareFolderModalContactPickerProps = {
    contactPickerPresentation: "modal" as const,
    contactSelectorSize: "compact" as const,
    hideCompactContactChrome: true,
  };

  const renderRecipientContactPreview = useCallback(
    (contact: Contact) =>
      shareFolderRecipientContactPreview(contact, matterId, () => {
        form.setValue(
          "matterContactConnections",
          form
            .getValues("matterContactConnections")
            .filter((p) => p.contactId !== contact.id),
          { shouldDirty: true },
        );
      }),
    [form, matterId],
  );

  const shareFolderRecipientsSidebar = !editing ? (
    <fieldset className="min-w-0 border-0 p-0">
      <AddContactFormField
        {...shareFolderModalContactPickerProps}
        register={form.register("matterContactConnections")}
        label="Recipients*"
        allowMultiple
        matterId={matterId}
        parentSlideoverOpen={open}
        onDelete={(contact) => {
          form.setValue(
            "matterContactConnections",
            form
              .getValues("matterContactConnections")
              .filter((p) => p.contactId !== contact.id),
            { shouldDirty: true },
          );
        }}
        renderSelectedContactPreview={renderRecipientContactPreview}
      />
    </fieldset>
  ) : null;

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={title}
          description={description}
          size={editing ? "2xl" : "3xl"}
          loading={isSubmitting}
          isDirty={form.formState.isDirty}
          preview={shareFolderRecipientsSidebar}
          previewLabel={null}
          bodyFormId={SHARE_FOLDER_MODAL_FORM_ID}
          bodyFormOnSubmit={form.handleSubmit(handleSubmit)}
          footer={
            <div className="flex w-full flex-wrap items-center justify-end gap-2">
              <FormCancelButton
                setOpen={setOpen}
                isDirty={form.formState.isDirty}
              />
              <FormSubmitButton
                submitFormId={SHARE_FOLDER_MODAL_FORM_ID}
                title={editing ? "Add files" : "Create"}
                loading={isSubmitting}
                disabled={!canCreateShareFolderFiles}
              />
            </div>
          }
        >
          <div className="overflow-y-auto">
            <fieldset
              className={cn(
                slideoverFieldsetClassName,
                "my-0 p-4 sm:p-5",
                "grid-cols-1 md:grid-cols-2",
              )}
            >
              {isSubmitting && <FormLoadingOverlay />}
              <div className="col-span-2 flex flex-col gap-6">
                {!editing && (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <TextFormField
                      register={form.register("matterShareFolderLabel")}
                      label="Folder label*"
                    />
                    <DateFormField
                      register={form.register(
                        "matterShareFolderExpirationDate",
                      )}
                      name="matterShareFolderExpirationDate"
                      label="Expiration date*"
                    />
                  </div>
                )}

                <div>
                  <div className="mb-2 text-sm font-medium">
                    <LabelText>Files*</LabelText>
                  </div>
                  {editing && existingFileCount > 0 && (
                    <div className="bg-muted/40 mb-4 rounded-md p-3">
                      <Typography className="text-xs tracking-wide uppercase">
                        Existing files ({existingFileCount})
                      </Typography>
                      <ul className="mt-2 space-y-1 text-sm">
                        {existingShareFolderFiles.map((file) => (
                          <li key={file.id} className="truncate">
                            {matter?.files.find(
                              (matterFile) => matterFile.id === file.fileId,
                            )?.name ?? "Untitled file"}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selectedFiles.length > 0 ? (
                    <div className="space-y-2">
                      {selectedFiles.map((file) => {
                        const FileIcon = getFileIcon(file.name);
                        return (
                          <div
                            key={file.id}
                            className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                          >
                            <div className="flex items-center gap-x-2">
                              <FileIcon className="h-4 w-4 text-blue-500" />
                              <span className="truncate">{file.name}</span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                handleRemoveDocument(file.id);
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                        );
                      })}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setIsSelectDocumentOpen(true);
                        }}
                      >
                        {editing ? "Select additional files" : "Add more files"}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsSelectDocumentOpen(true);
                      }}
                    >
                      {editing ? "Select files to add" : "Choose files"}
                    </Button>
                  )}
                  {form.formState.errors.matterFileIds && (
                    <p className="text-destructive mt-2 text-xs">
                      {form.formState.errors.matterFileIds.message}
                    </p>
                  )}
                </div>
              </div>
            </fieldset>
          </div>
        </AppModal>
      </Form>

      {matter && (
        <SelectDocumentModal
          open={isSelectDocumentOpen}
          setOpen={setIsSelectDocumentOpen}
          matter={matter}
          onDocumentSelected={handleDocumentSelected}
          enabledFileTypes={undefined}
          multiple
          maxSelection={10}
          excludedFileIds={editing ? existingShareFolderFileIds : undefined}
          description="Choose files to add to the share folder"
        />
      )}
    </>
  );
};

CreateOrEditShareFolderModal.displayName = "CreateOrEditShareFolderModal";
